import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ CRW-03 at v1.9107, crew part 4E: what a person can do — the roles they can work, their
// skills, an employment type and a tier — the tenant's lists of skills and employment types, and the
// signed-in user's own crew order.

export type ListEntry = components['schemas']['ListEntryView'];
export type Abilities = {
  roleIds: string[];
  skillIds: string[];
  employmentTypeId: string | null;
  tier: number | null;
};
export type CrewOrder = components['schemas']['PreferencesView']['crewOrder'];
export type ListKind = 'skills' | 'employment-types';

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

/** A list, retired entries included, so a person's retired skill still reads by name. */
export const listQuery = (kind: ListKind) =>
  queryOptions({
    queryKey: [kind],
    queryFn: async (): Promise<ListEntry[]> => {
      const { data, error } =
        kind === 'skills'
          ? await api.GET('/v1/skills', { params: { query: { includeRetired: true } } })
          : await api.GET('/v1/employment-types', { params: { query: { includeRetired: true } } });
      if (error) throw problem(error, 'Could not load the list.');
      return data;
    },
  });

export async function addListEntry(kind: ListKind, name: string) {
  const { data, error } =
    kind === 'skills'
      ? await api.POST('/v1/skills', { body: { name } })
      : await api.POST('/v1/employment-types', { body: { name } });
  if (error) throw problem(error, 'Could not add that.');
  return data;
}

export async function changeListEntry(
  kind: ListKind,
  id: string,
  changes: { name?: string; retired?: boolean },
) {
  const { data, error } =
    kind === 'skills'
      ? await api.PATCH('/v1/skills/{id}', { params: { path: { id } }, body: changes })
      : await api.PATCH('/v1/employment-types/{id}', { params: { path: { id } }, body: changes });
  if (error) throw problem(error, 'Could not change that.');
  return data;
}

/** Each list replaces what was there. */
export async function setAbilities(crewMemberId: string, abilities: Abilities) {
  const { data, error } = await api.PUT('/v1/crew/{id}/abilities', {
    params: { path: { id: crewMemberId } },
    body: abilities,
  });
  if (error) throw problem(error, 'Could not save what they can do.');
  return data;
}

export const preferencesQuery = queryOptions({
  queryKey: ['my', 'preferences'],
  queryFn: async () => {
    const { data, error } = await api.GET('/v1/my/preferences');
    if (error) throw problem(error, 'Could not load your preferences.');
    return data;
  },
});

export async function setCrewOrder(crewOrder: CrewOrder) {
  const { data, error } = await api.PUT('/v1/my/preferences', { body: { crewOrder } });
  if (error) throw problem(error, 'Could not save that.');
  return data;
}

// — The eligible-crew filter (crew part 4E-3) —

export type Eligible = components['schemas']['EligibleView'];

/** What the work is: a shift or offer, as entered, in its place's zone. */
export interface WorkContext {
  readonly start: string;
  readonly end: string;
  readonly venueId?: string | null | undefined;
  readonly locationId?: string | null | undefined;
  readonly roleId?: string | null | undefined;
  readonly jobId?: string | null | undefined;
  readonly excludeShiftId?: string | null | undefined;
  readonly excludeOfferId?: string | null | undefined;
}

export const eligibleQuery = (c: WorkContext) => {
  const query = Object.fromEntries(
    Object.entries(c).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  ) as { start: string; end: string } & Record<string, string>;
  return queryOptions({
    queryKey: ['crew', 'eligible', query],
    queryFn: async (): Promise<Eligible[]> => {
      const { data, error } = await api.GET('/v1/crew/eligible', { params: { query } });
      if (error) throw problem(error, 'Could not load who could do it.');
      return data;
    },
    staleTime: 30_000,
  });
};
